import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "./ProductDetail.module.css";
import { useCart } from "../../Context/CartContext";

const Stars = ({ rating }) => {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return (
    <span className={styles.starsWrap}>
      {"★".repeat(full)}
      {half ? "½" : ""}
      {"☆".repeat(Math.max(0, 5 - full - (half ? 1 : 0)))}
    </span>
  );
};

const ProductDetail = () => {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const { addToCart, cartCount } = useCart();

  const [product,     setProduct]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [activeImg,   setActiveImg]   = useState(0);
  const [qty,         setQty]         = useState(1);
  const [added,       setAdded]       = useState(false);
  const [newReview,   setNewReview]   = useState({ rating: 5, comment: "" });
  const [submitting,  setSubmitting]  = useState(false);
  const [patientName, setPatientName] = useState("Verified Patient");

  // Determine logged-in patient name
  useEffect(() => {
    const resolvePatientName = async () => {
      let storedName = localStorage.getItem("userName");
      if (!storedName || storedName === "undefined" || storedName === "null" || storedName === "Patient") {
        try {
          const uid = localStorage.getItem("userId");
          const token = localStorage.getItem("token");
          if (uid && token) {
            const res = await axios.get(`/api/patient/${uid}`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.data?.patient) {
              const p = res.data.patient;
              storedName = p.name || `${p.firstName || ""} ${p.lastName || ""}`.trim() || "Verified Patient";
              localStorage.setItem("userName", storedName);
            }
          }
        } catch (e) {
          storedName = "Verified Patient";
        }
      }
      setPatientName(storedName || "Verified Patient");
    };
    resolvePatientName();
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/shop/products/${id}`);
        setProduct(res.data.product);
      } catch (err) {
        console.error("Failed to fetch product detail", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    if (!product) return;
    addToCart(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!newReview.comment.trim()) return;
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Please sign in to submit a review.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `/api/shop/products/${id}/review`,
        {
          rating: newReview.rating,
          comment: newReview.comment,
          userName: patientName,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status) {
        setProduct(res.data.product);
        setNewReview({ rating: 5, comment: "" });
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  if (loading) {
    return (
      <div className={styles.pageContainer} style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "80vh" }}>
        <div style={{ textAlign: "center", color: "#64748b" }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>⏳</div>
          <h3 style={{ fontWeight: 800 }}>Loading product details...</h3>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className={styles.pageContainer} style={{ textAlign: "center", padding: "4rem 1rem" }}>
        <h3 style={{ fontWeight: 800 }}>Product Not Found</h3>
        <button onClick={() => navigate("/shop")} className={styles.backBtn} style={{ margin: "1rem auto" }}>
          ← Back to Shop
        </button>
      </div>
    );
  }

  const images = [product.coverImage, ...(product.galleryImages || [])].filter(Boolean);
  const displayImg = images[activeImg] || product.coverImage || "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&q=60";

  return (
    <div className={styles.pageContainer}>
      {/* Sticky Top Nav */}
      <nav className={styles.detailNav}>
        <button className={styles.backBtn} onClick={() => navigate("/shop")}>
          ← Back to Shop
        </button>
        <button className={styles.cartBtn} onClick={() => navigate("/shop/checkout")} title="Shopping Cart">
          🛒
          {cartCount > 0 && <span className={styles.cartBadge}>{cartCount}</span>}
        </button>
      </nav>

      {/* Main Product Layout */}
      <main className={styles.mainGrid}>
        {/* Gallery */}
        <div className={styles.galleryWrap}>
          <div className={styles.mainImageContainer}>
            <img
              src={displayImg}
              alt={product.title}
              className={styles.mainImage}
              onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&q=60"; }}
            />
          </div>
          {images.length > 1 && (
            <div className={styles.thumbStrip}>
              {images.map((imgUrl, idx) => (
                <img
                  key={idx}
                  src={imgUrl}
                  alt="thumbnail"
                  className={`${styles.thumb} ${activeImg === idx ? styles.thumbActive : ""}`}
                  onClick={() => setActiveImg(idx)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Product Info & Action */}
        <div className={styles.infoWrap}>
          <span className={styles.categoryTag}>{product.category}</span>
          <h1 className={styles.productTitle}>{product.title}</h1>

          <div className={styles.ratingRow}>
            <Stars rating={product.averageRating || 0} />
            <span className={styles.ratingCount}>
              {product.averageRating ? `${product.averageRating.toFixed(1)} / 5` : "New"} ({product.totalReviews || 0} reviews)
            </span>
          </div>

          <div className={styles.priceSection}>
            <span className={styles.priceMain}>₹{(product.price * 83).toFixed(0)}</span>
            <span className={styles.priceSub}>(${product.price.toFixed(2)} USD)</span>
            <span className={`${styles.stockBadge} ${product.stockQty > 0 ? styles.inStock : styles.outStock}`}>
              {product.stockQty > 0 ? `✓ In Stock (${product.stockQty})` : "✕ Out of Stock"}
            </span>
          </div>

          <p className={styles.desc}>{product.description}</p>

          {product.features && product.features.length > 0 && (
            <div className={styles.featuresSection}>
              <h3 className={styles.featuresTitle}>Highlights & Features</h3>
              <ul className={styles.featuresList}>
                {product.features.map((feat, idx) => (
                  <li key={idx} className={styles.featureItem}>
                    <span className={styles.featureIcon}>✓</span>
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {product.stockQty > 0 && (
            <div className={styles.ctaRow}>
              <div className={styles.qtyBox}>
                <button className={styles.qtyBtn} onClick={() => setQty((q) => Math.max(1, q - 1))}>−</button>
                <span className={styles.qtyVal}>{qty}</span>
                <button className={styles.qtyBtn} onClick={() => setQty((q) => Math.min(product.stockQty, q + 1))}>+</button>
              </div>
              <button className={`${styles.addBtn} ${added ? styles.addBtnSuccess : ""}`} onClick={handleAddToCart}>
                {added ? "✓ Added to Cart!" : "🛒 Add to Cart"}
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Reviews Section */}
      <section className={styles.reviewsContainer}>
        <div className={styles.reviewsCard}>
          <div className={styles.reviewsHeader}>
            <h2 className={styles.reviewsTitle}>Patient Reviews ({product.totalReviews || 0})</h2>
          </div>

          {product.reviews && product.reviews.length > 0 ? (
            <div className={styles.reviewList}>
              {product.reviews.map((r, i) => (
                <div key={i} className={styles.reviewItem}>
                  <div className={styles.reviewerRow}>
                    <div className={styles.avatar}>{(r.userName || "P")[0].toUpperCase()}</div>
                    <span className={styles.reviewerName}>{r.userName || "Verified Patient"}</span>
                    <span style={{ color: "#f59e0b", fontSize: "0.9rem", marginLeft: "6px" }}>{"★".repeat(r.rating)}</span>
                    <span className={styles.reviewDate}>{r.date ? formatDate(r.date) : ""}</span>
                  </div>
                  <p className={styles.reviewComment}>{r.comment}</p>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: "#94a3b8", textAlign: "center", padding: "1.5rem 0" }}>No reviews yet. Share your experience below!</p>
          )}

          {/* Secure Write Review Form (No custom Name Input) */}
          <div className={styles.formCard}>
            <h3 style={{ margin: "0 0 0.75rem", fontSize: "1.05rem", fontWeight: 800 }}>Write a Review</h3>
            <div className={styles.patientBadge}>
              <span>👤 Reviewing as:</span>
              <strong style={{ color: "#2d6a4f" }}>{patientName}</strong>
            </div>

            <form onSubmit={handleReviewSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "#64748b", marginBottom: "6px" }}>Your Rating:</label>
                <div className={styles.starSelector}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setNewReview((p) => ({ ...p, rating: star }))}
                      className={styles.starBtn}
                      style={{ color: star <= newReview.rating ? "#f59e0b" : "#cbd5e1" }}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "#64748b", marginBottom: "6px" }}>Your Experience:</label>
                <textarea
                  rows={3}
                  className={styles.commentInput}
                  placeholder="Tell us about product quality, delivery, or results..."
                  value={newReview.comment}
                  onChange={(e) => setNewReview((p) => ({ ...p, comment: e.target.value }))}
                />
              </div>

              <button type="submit" disabled={submitting} className={styles.submitBtn}>
                {submitting ? "Submitting..." : "Submit Review"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
